import { useEffect, useState } from "react";
import { api } from "../../api/client";
import { MONTHS, YEARS, money, todayParts } from "../../constants";
import {
  Button,
  DataCard,
  Icon,
  PageTitle,
  Select,
  Spinner,
  Td,
  Th
} from "../../components/ui";
import { KunciPayrollBar, useKunciPayroll } from "./KunciPayroll";

export default function AdminSalaryPage({ setToast, isAdmin = false, isSuperAdmin = false }) {
  const today = todayParts();

  const [filters, setFilters] = useState({
    year: today.year,
    month: today.month,
    trainer: "All"
  });

  // Kunci payroll BERSAMA (trainer + staff) untuk bulan yang sedang dilihat.
  const bulanNum = MONTHS.indexOf(filters.month) + 1;
  const { kunci, muat: muatKunci } = useKunciPayroll(Number(filters.year), bulanNum);
  const terkunci = !!kunci?.terkunci;

  const [trainers, setTrainers] = useState([]);
  const [positions, setPositions] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  async function loadStaticData() {
    try {
      const [trainerRes, posRes] = await Promise.all([
        api.getTrainerList(),
        api.getSalaryPositions()
      ]);

      if (trainerRes.success) setTrainers(trainerRes.data || []);
      if (posRes.success) setPositions(posRes.data || []);
    } catch {
      setTrainers([]);
      setPositions([]);
    }
  }

  async function load() {
    setLoading(true);

    try {
      const res = await api.getAdminSalaryData(filters);

      if (res.success) {
        setRows(res.data || []);
      } else {
        setToast({
          type: "error",
          message: res.message || "Failed to load salary"
        });
      }
    } catch (err) {
      setToast({
        type: "error",
        message: err.message || "Failed to load salary"
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadStaticData();
  }, []);

  useEffect(() => {
    load();
  }, [filters]);

  // `berlakuSemua` false = bracket ini hanya untuk SESI baris ini. Itu yang benar
  // secara bawaan: satu trainer bisa pegang kelas reguler dan kelas TOEFL yang
  // tarifnya berbeda. Dulu setiap perubahan selalu mengenai semua sesi trainer itu,
  // sehingga dua kelas mustahil dibedakan dan angkanya "kembali sama" sendiri.
  // Berapa baris (sesi) yang dimiliki tiap trainer di tampilan ini. Tombol "samakan
  // semua sesi" hanya berguna — dan hanya muncul — kalau trainernya memang punya
  // lebih dari satu.
  const sesiPerTrainer = rows.reduce((a, r) => {
    const k = String(r.name || "").toLowerCase();
    a[k] = (a[k] || 0) + 1;
    return a;
  }, {});

  async function changePosition(row, newPosition, berlakuSemua = false) {
    try {
      const res = await api.updateSalaryPositionAndRecalculate({
        year: row.year,
        month: row.month,
        trainer: row.name,
        session: row.session,
        newPosition,
        berlakuSemua
      });

      setToast({
        type: res.success ? "success" : "error",
        message: res.message || "Done"
      });

      if (res.success) load();
    } catch (err) {
      setToast({
        type: "error",
        message: err.message || "Recalculate failed"
      });
    }
  }

  // ── Ekspor payroll ──────────────────────────────────────────
  //
  // Dulu CSV mentah: 31 kolom bernomor tanpa tanggal, tanpa baris total, dan
  // angkanya jadi teks. Di Excel berlokal Indonesia — yang memakai koma sebagai
  // pemisah desimal — berkas CSV berkoma juga rawan terbaca jadi satu kolom.
  //
  // Sekarang xlsx, sama gayanya dengan ekspor Salary Adjustments: angkanya benar-
  // benar angka (masih bisa dijumlah di Excel), kepala kolom menyebut tanggal DAN
  // harinya, dan ada baris total per hari.
  async function exportExcel() {
    if (!rows.length) {
      setToast({ type: "error", message: "Tak ada data untuk diunduh." });
      return;
    }
    try {
      const ExcelJS = (await import("exceljs")).default;
      const NAVY = "FF0D59F2";
      const RP = '"Rp" #,##0';

      const tahun = Number(filters.year);
      const iBulan = Math.max(0, MONTHS.indexOf(filters.month));
      const jmlHari = new Date(tahun, iBulan + 1, 0).getDate();
      const HARI = ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"];
      const kolomHari = Array.from({ length: jmlHari }, (_, i) => {
        const d = new Date(tahun, iBulan, i + 1).getDay();
        return { tgl: i + 1, nama: HARI[d], pekan: d === 0 || d === 6 };
      });

      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("Trainer Payroll");
      const periode = `${filters.month} ${filters.year}`;
      const lebar = 3 + jmlHari + 1;

      ws.mergeCells(1, 1, 1, lebar);
      const judul = ws.getCell(1, 1);
      judul.value =
        `Trainer Payroll — ${periode}` +
        (filters.trainer && filters.trainer !== "All" ? ` · ${filters.trainer}` : "");
      judul.font = { bold: true, size: 14, color: { argb: NAVY } };
      judul.alignment = { vertical: "middle" };
      ws.getRow(1).height = 24;
      ws.addRow([]);

      // Dua baris kepala: nama hari di atas, tanggal di bawah. Satu baris berisi
      // "1..31" saja memaksa pembacanya menghitung sendiri tanggal berapa itu.
      const kHari = ws.addRow(["Nama", "Sesi", "Jabatan", ...kolomHari.map((k) => k.nama), "Total"]);
      const kTgl = ws.addRow(["", "", "", ...kolomHari.map((k) => k.tgl), ""]);
      [kHari, kTgl].forEach((r) => {
        r.eachCell((cell) => {
          cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 10 };
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: NAVY } };
          cell.alignment = { horizontal: "center", vertical: "middle" };
        });
        r.height = 18;
      });
      [1, 2, 3, lebar].forEach((k) => {
        ws.mergeCells(3, k, 4, k);
        ws.getCell(3, k).alignment = { horizontal: "center", vertical: "middle", wrapText: true };
      });

      const totalHarian = Array.from({ length: jmlHari }, () => 0);
      rows.forEach((row) => {
        const hari = row.days || [];
        const baris = ws.addRow([
          row.name,
          row.session,
          row.position,
          ...kolomHari.map((k) => {
            const v = Number(hari[k.tgl - 1] || 0);
            totalHarian[k.tgl - 1] += v;
            // Nol dibiarkan KOSONG. Sebulan berisi 31 kolom yang sebagian besar
            // nol; menuliskannya semua membuat hari yang benar-benar dibayar
            // tenggelam di antara angka nol.
            return v > 0 ? v : null;
          }),
          Number(row.total || 0)
        ]);
        baris.eachCell((cell, k) => {
          if (k > 3) cell.numFmt = RP;
          if (k > 3 && k < lebar && kolomHari[k - 4] && kolomHari[k - 4].pekan) {
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF6F7F9" } };
          }
        });
        baris.getCell(lebar).font = { bold: true };
      });

      ws.addRow([]);
      const total = ws.addRow([
        "TOTAL", "", "",
        ...totalHarian.map((v) => (v > 0 ? v : null)),
        totalHarian.reduce((a, v) => a + v, 0)
      ]);
      total.eachCell((cell, k) => {
        cell.font = { bold: true };
        cell.border = { top: { style: "double", color: { argb: NAVY } } };
        if (k > 3) cell.numFmt = RP;
      });

      ws.columns = [
        { width: 24 }, { width: 18 }, { width: 20 },
        ...kolomHari.map(() => ({ width: 11 })),
        { width: 15 }
      ];
      // Kepala DAN tiga kolom kiri dibekukan: tabel selebar 31 hari tak bisa
      // dibaca kalau nama trainernya ikut tergulir keluar layar.
      ws.views = [{ state: "frozen", xSplit: 3, ySplit: 4 }];

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const siapa =
        filters.trainer && filters.trainer !== "All" ? ` ${filters.trainer}` : "";
      a.download = `Trainer Payroll ${periode}${siapa}.xlsx`.replace(/[\\/:*?"<>|]/g, "");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setToast({ type: "error", message: err.message || "Gagal mengunduh" });
    }
  }

  return (
    <>
      <PageTitle
        title="Trainer Payroll"
        subtitle="Automated ledger of daily shift earnings and total monthly pay."
        action={
          <div className="flex flex-wrap gap-2">
            <Select
              value={filters.year}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  year: e.target.value
                })
              }
              className="w-28"
            >
              {YEARS.map((year) => (
                <option key={year}>{year}</option>
              ))}
            </Select>

            <Select
              value={filters.month}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  month: e.target.value
                })
              }
              className="w-40"
            >
              {MONTHS.map((month) => (
                <option key={month}>{month}</option>
              ))}
            </Select>

            <Select
              value={filters.trainer}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  trainer: e.target.value
                })
              }
              className="w-40"
            >
              <option>All</option>
              {trainers.map((trainer) => (
                <option key={trainer}>{trainer}</option>
              ))}
            </Select>

            <Button variant="success" onClick={exportExcel} disabled={loading || !rows.length}>
              <Icon name="download" />
              Excel
            </Button>
          </div>
        }
      />

      <KunciPayrollBar
        kunci={kunci}
        year={Number(filters.year)}
        month={bulanNum}
        bisaKunci={isAdmin}
        bisaBuka={isSuperAdmin}
        setToast={setToast}
        onBerubah={() => {
          muatKunci();
          load();
        }}
      />

      {loading ? (
        <Spinner />
      ) : (
        <DataCard>
          <table className="w-full whitespace-nowrap text-left">
            <thead>
              <tr>
                <Th sticky>STAFF & SESSION</Th>
                {Array.from({ length: 31 }, (_, index) => (
                  <Th key={index} center>
                    {index + 1}
                  </Th>
                ))}
                <Th right stickyRight>
                  TOTAL
                </Th>
              </tr>
            </thead>

            <tbody>
              {rows.map((row, index) => (
                <tr
                  key={`${row.name}-${row.session}-${index}`}
                  className="border-t border-slate-200 dark:border-slate-800"
                >
                  <Td sticky>
                    <p className="font-black">{row.name}</p>
                    <p className="text-[10px] text-slate-500">{row.session}</p>

                    <Select
                      key={`${row.name}-${row.session}-${row.position}`}
                      defaultValue={row.position}
                      onChange={(e) => changePosition(row, e.target.value)}
                      disabled={terkunci}
                      className="mt-1 w-40 text-xs"
                      title={terkunci ? "Bulan ini sudah dikunci" : "Berlaku untuk sesi ini saja"}
                    >
                      {Array.from(
                        new Set([row.position, ...positions].filter(Boolean))
                      ).map((position) => (
                        <option key={position}>{position}</option>
                      ))}
                    </Select>
                    {!terkunci && sesiPerTrainer[String(row.name).toLowerCase()] > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm(
                            `Samakan bracket SEMUA sesi ${row.name} di cabang ini menjadi "${row.position}"?`
                          )) changePosition(row, row.position, true);
                        }}
                        className="mt-0.5 block text-[10px] font-bold text-slate-400 underline hover:text-primary"
                        title="Tanpa ini, perubahan bracket hanya mengenai sesi baris ini"
                      >
                        samakan semua sesi
                      </button>
                    )}
                  </Td>

                  {(row.days || []).map((amount, dayIndex) => (
                    <Td key={dayIndex} center mono>
                      {money(amount)}
                    </Td>
                  ))}

                  <Td right stickyRight className="font-black text-primary">
                    {money(row.total)}
                  </Td>
                </tr>
              ))}

              {!rows.length && (
                <tr>
                  <Td colSpan="33">No salary records found.</Td>
                </tr>
              )}
            </tbody>
          </table>
        </DataCard>
      )}
    </>
  );
}